import React, { useState, useRef, useEffect } from 'react';
import './ImageCropperModal.css';
import { clampCropPosition, minimumScaleForCircle, cropExportGeometry } from './avatarCropGeometry';

const ImageCropperModal = ({ file, imageUrl, onCrop, close }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  // Track load state so a failed image never leaves the user staring at an
  // empty frame with no explanation and no way to tell it apart from loading.
  const [imageReady, setImageReady] = useState(false);
  const [loadError, setLoadError] = useState(null);
  
  // Use refs for drag state to avoid closure staleness and re-render issues
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const getSafePosition = React.useCallback((nextPosition, nextScale = scale) => {
    const image = imageRef.current;
    if (!image?.naturalWidth || !image?.naturalHeight) return nextPosition;
    return clampCropPosition(nextPosition, image.naturalWidth, image.naturalHeight, nextScale);
  }, [scale]);

  useEffect(() => {
    setImageReady(false);
    setLoadError(null);

    if (file) {
      // An empty/zero-byte File means the caller handed us something unusable
      // (blocked fetch, cancelled picker, unreadable file). Fail loudly.
      if (typeof file.size === 'number' && file.size === 0) {
        setLoadError('That image could not be read. Please pick a different file.');
        return;
      }
      const url = URL.createObjectURL(file);
      setImageSrc(url);
      return () => URL.revokeObjectURL(url);
    } else if (imageUrl) {
      setImageSrc(imageUrl);
    } else {
      setLoadError('No image was provided to crop.');
    }
  }, [file, imageUrl]);

  const handleWindowMouseMove = React.useCallback((e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const newX = e.clientX - dragStart.current.x;
    const newY = e.clientY - dragStart.current.y;
    setPosition(getSafePosition({ x: newX, y: newY }));
  }, [getSafePosition]);

  const handleWindowTouchMove = React.useCallback((e) => {
    if (!isDragging.current || !e.touches || e.touches.length === 0) return;
    const t = e.touches[0];
    const newX = t.clientX - dragStart.current.x;
    const newY = t.clientY - dragStart.current.y;
    setPosition(getSafePosition({ x: newX, y: newY }));
  }, [getSafePosition]);

  const handleDragEnd = React.useCallback(() => {
    if (isDragging.current) {
      isDragging.current = false;
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('touchmove', handleWindowTouchMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchend', handleDragEnd);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleWindowMouseMove, handleWindowTouchMove]);

  const handleMouseDown = (e) => {
    e.preventDefault(); // Prevent default drag behavior
    isDragging.current = true;
    dragStart.current = { x: e.clientX - position.x, y: e.clientY - position.y };
    window.addEventListener('mousemove', handleWindowMouseMove);
    window.addEventListener('mouseup', handleDragEnd);
  };

  const handleTouchStart = (e) => {
    if (!e.touches || e.touches.length === 0) return;
    const t = e.touches[0];
    isDragging.current = true;
    dragStart.current = { x: t.clientX - position.x, y: t.clientY - position.y };
    window.addEventListener('touchmove', handleWindowTouchMove, { passive: false });
    window.addEventListener('touchend', handleDragEnd);
  };

  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('touchmove', handleWindowTouchMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [handleWindowMouseMove, handleWindowTouchMove, handleDragEnd]);

  const handleWheel = (e) => {
    // Prevent page scroll
    e.stopPropagation(); 
    // e.preventDefault(); // React synthetic events might not support preventDefault on wheel in some versions/browsers passively
    
    const zoomSpeed = 0.001;
    const image = imageRef.current;
    const minimumScale = minimumScaleForCircle(image?.naturalWidth, image?.naturalHeight);
    const newScale = Math.min(Math.max(minimumScale, scale + (e.deltaY * -zoomSpeed)), 10);
    setScale(newScale);
    setPosition(current => getSafePosition(current, newScale));
  };

  const handleSave = () => {
    // Previously this returned silently when the image had not loaded, so the
    // user clicked "Save Avatar" and absolutely nothing happened.
    if (!imageRef.current || !imageReady) {
      setLoadError('The image is not ready yet. Please wait for it to load, or pick another file.');
      return;
    }

    const geometry = cropExportGeometry(position, scale);
    if (!geometry) {
      setLoadError('Failed to crop image. Please try a different photo.');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = geometry.outputSize;
    canvas.height = geometry.outputSize;
    const ctx = canvas.getContext('2d');
    
    // Enable high quality scaling
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const img = imageRef.current;
    // Keep the chosen square framing and source transparency;
    // the circular mask is preview CSS only.

    // Move context to center + offset
    ctx.translate(geometry.translateX, geometry.translateY);
    ctx.scale(geometry.drawScale, geometry.drawScale);
    
    // Draw image centered at the new origin
    ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
    
    // A cross-origin image that failed CORS taints the canvas, and toBlob()
    // throws a SecurityError. Catch it so the user gets a real message.
    try {
      canvas.toBlob((blob) => {
        if (blob) {
          const croppedFile = new File([blob], "avatar.png", { type: "image/png" });
          const croppedUrl = URL.createObjectURL(blob);
          console.log('[ImageCropper] Created blob URL:', croppedUrl);
          onCrop(croppedFile, croppedUrl);
          close();
        } else {
          setLoadError('Failed to crop image. Please try a different photo.');
        }
      }, 'image/png', 1.0);
    } catch (err) {
      console.error('[ImageCropper] toBlob failed', err);
      setLoadError('This image could not be processed. Try uploading it from your device instead.');
    }
  };

  return (
    <div className="image-cropper-modal">
      <h3>Adjust Avatar</h3>
      <div 
        className="cropper-container" 
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onWheel={handleWheel}
      >
        {imageSrc && !loadError && (
          <img 
            ref={imageRef}
            src={imageSrc} 
            alt="Crop target" 
            className="cropper-image"
            crossOrigin="anonymous"
            onLoad={(event) => {
              const { naturalWidth, naturalHeight } = event.currentTarget;
              // Default: centered image, zoomed out as far as possible while
              // still covering every pixel of the circular output.
              setScale(minimumScaleForCircle(naturalWidth, naturalHeight));
              setPosition({ x: 0, y: 0 });
              setImageReady(true);
              setLoadError(null);
            }}
            onError={() => {
              setImageReady(false);
              setLoadError('That image could not be loaded. Please pick a different file.');
            }}
            style={{
              left: '50%',
              top: '50%',
              transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${scale})`
            }}
            draggable={false}
          />
        )}
        {!imageSrc && !loadError && (
          <p className="cropper-status">Loading image…</p>
        )}
        {loadError && (
          <p className="cropper-error" role="alert">{loadError}</p>
        )}
        <div className="cropper-overlay"></div>
      </div>
      
      <div className="cropper-controls">
        <span>Zoom:</span>
        <input 
          type="range" 
          min={minimumScaleForCircle(imageRef.current?.naturalWidth, imageRef.current?.naturalHeight)}
          max="5" 
          step="any"
          value={scale} 
          onChange={(e) => {
            const nextScale = parseFloat(e.target.value);
            setScale(nextScale);
            setPosition(current => getSafePosition(current, nextScale));
          }}
          aria-label="Avatar zoom"
          className="zoom-slider"
        />
      </div>

      <div className="cropper-actions">
        <button className="btn-cancel" onClick={close}>Cancel</button>
        <button
          className="btn-save"
          onClick={handleSave}
          disabled={!imageReady || Boolean(loadError)}
        >
          Save Avatar
        </button>
      </div>
    </div>
  );
};

export default ImageCropperModal;
